import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AuthStateService } from '../../../core/services/auth-state/auth-state-service.service';

/**
 * OrdersActionsBarComponent
 * Barra de acciones para operaciones masivas
 * Responsabilidad: Acciones sobre órdenes seleccionadas
 * ✅ FASE B3: Completo con integración de AuthStateService
 */
@Component({
  selector: 'app-orders-actions-bar',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule
  ],
  templateUrl: './orders-actions-bar.component.html',
  styleUrls: ['./orders-actions-bar.component.css']
})
export class OrdersActionsBarComponent {
  
  @Input() selectedCount: number = 0;
  @Input() typeOrders: string = '';
  // ✅ FASE B3: userRole eliminado - ahora usa AuthStateService internamente
  
  @Output() exportSelected = new EventEmitter<void>();
  @Output() deleteSelected = new EventEmitter<void>();
  @Output() changeStatusSelected = new EventEmitter<void>();
  @Output() assignSelected = new EventEmitter<void>();
  @Output() clearSelection = new EventEmitter<void>();
  @Output() addNew = new EventEmitter<void>();
  @Output() exportAll = new EventEmitter<void>();
  @Output() importData = new EventEmitter<void>();

  constructor(private authStateService: AuthStateService) {
  }

  /**
   * Verifica si se puede exportar
   */
  canExport(): boolean {
    return this.selectedCount > 0;
  }

  /**
   * Verifica si se puede eliminar
   */
  canDelete(): boolean {
    return this.selectedCount > 0 && this.hasDeletePermission();
  }

  /**
   * Verifica si se puede cambiar estado
   */
  canChangeStatus(): boolean {
    return this.selectedCount > 0 && this.hasStatusPermission();
  }

  /**
   * Verifica si se puede asignar
   */
  canAssign(): boolean {
    return this.selectedCount > 0 && this.hasAssignPermission();
  }

  /**
   * Verifica si se puede agregar nuevo
   */
  canAddNew(): boolean {
    return this.hasCreatePermission();
  }

  /**
   * Verifica si se puede importar
   */
  canImport(): boolean {
    return this.hasImportPermission();
  }

  // ✅ FASE B3: Métodos de permisos mejorados con AuthStateService
  private hasDeletePermission(): boolean {
    const role = this.authStateService.getRole()?.toLowerCase() || '';
    return ['admin', 'supervisor'].includes(role);
  }

  private hasStatusPermission(): boolean {
    // Cualquier usuario autenticado puede cambiar estado
    const role = this.authStateService.getRole();
    return role !== null && role !== '';
  }

  private hasAssignPermission(): boolean {
    const role = this.authStateService.getRole()?.toLowerCase() || '';
    return ['admin', 'supervisor', 'manager'].includes(role);
  }

  private hasCreatePermission(): boolean {
    // Brokers pueden crear pedidos (excepto en vistas de cancelados/congelados)
    return this.typeOrders !== 'cancelled' && this.typeOrders !== 'frozen';
  }

  private hasImportPermission(): boolean {
    // Los usuarios con rol de Admin o Agentes Supervisor pueden importar
    // Solo cuentas de tipo supplier
    const role = this.authStateService.getRole()?.toLowerCase() || '';
    const accountType = this.authStateService.getAccountType()?.toLowerCase() || '';
    return accountType === 'supplier' && ['admin', 'agent', 'supervisor'].includes(role);
  }

  // Manejadores de eventos
  onExportSelected(): void {
    if (this.canExport()) {
      this.exportSelected.emit();
    }
  }

  onDeleteSelected(): void {
    if (this.canDelete()) {
      this.deleteSelected.emit();
    }
  }

  onChangeStatusSelected(): void {
    if (this.canChangeStatus()) {
      this.changeStatusSelected.emit();
    }
  }

  onAssignSelected(): void {
    if (this.canAssign()) {
      this.assignSelected.emit();
    }
  }

  onClearSelection(): void {
    this.clearSelection.emit();
  }

  onAddNew(): void {
    if (this.canAddNew()) {
      this.addNew.emit();
    }
  }

  onExportAll(): void {
    this.exportAll.emit();
  }

  onImportData(): void {
    if (this.canImport()) {
      this.importData.emit();
    }
  }
}
